import React from 'react'
import './SurveySchemaBuilder.css'

const QUESTION_TYPE_OPTIONS = [
  { value: 'text', label: '한 줄 입력' },
  { value: 'long-text', label: '긴 글 입력' },
  { value: 'tel', label: '연락처' },
  { value: 'date', label: '날짜' },
  { value: 'number', label: '숫자' },
  { value: 'single-select', label: '하나 선택' },
  { value: 'multi-select', label: '복수 선택' },
  { value: 'select', label: '목록 선택' },
]

const OPTION_QUESTION_TYPES = new Set(['single-select', 'multi-select', 'select'])

function deepClone(value) {
  if (value == null) return value
  return JSON.parse(JSON.stringify(value))
}

function isOptionQuestion(type) {
  return OPTION_QUESTION_TYPES.has(String(type || '').toLowerCase())
}

function optionDisplayText(option) {
  if (typeof option === 'string' || typeof option === 'number') return String(option).trim()
  return String(option?.label || option?.value || '').trim()
}

function makeKey(prefix, existing = new Set()) {
  let key = `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
  while (existing.has(key)) {
    key = `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
  }
  return key
}

function allQuestionKeys(schema) {
  return new Set(
    (Array.isArray(schema?.sections) ? schema.sections : []).flatMap((section) =>
      (Array.isArray(section?.questions) ? section.questions : [])
        .map((question) => String(question?.key || '').trim())
        .filter(Boolean),
    ),
  )
}

function allSectionKeys(schema) {
  return new Set(
    (Array.isArray(schema?.sections) ? schema.sections : [])
      .map((section) => String(section?.key || '').trim())
      .filter(Boolean),
  )
}

export function createEmptySurveySchema() {
  return {
    title: '',
    description: '',
    sections: [{
      key: 'main',
      title: '설문 항목',
      description: '',
      questions: [],
    }],
  }
}

export function prepareImportedSurveySchema(schema, sourceName = '') {
  const next = deepClone(schema || createEmptySurveySchema()) || createEmptySurveySchema()
  const baseTitle = String(next.title || sourceName || '가져온 설문').trim()
  next.title = baseTitle ? `${baseTitle} 사본` : '새 설문'
  if (!Array.isArray(next.sections) || next.sections.length === 0) {
    next.sections = createEmptySurveySchema().sections
  }
  next.sections = next.sections.map((section, index) => ({
    ...section,
    key: String(section?.key || '').trim() || `section_${index + 1}`,
    title: String(section?.title || '').trim() || `설문 항목 ${index + 1}`,
    description: section?.description || '',
    questions: Array.isArray(section?.questions)
      ? section.questions.map((question) => {
        const nextQuestion = question && typeof question === 'object' ? { ...question } : {}
        if (Array.isArray(nextQuestion.options)) {
          nextQuestion.options = nextQuestion.options.map(optionDisplayText).filter(Boolean)
        }
        return nextQuestion
      })
      : [],
  }))
  return next
}

function prepareEditableSurveySchema(schema, fallbackName = '') {
  const next = prepareImportedSurveySchema(schema, '')
  next.title = String(schema?.title || fallbackName || '').trim()
  return next
}

function buildInitialSurveySchema(initialSchema, initialName, preserveInitialTitle) {
  if (initialSchema) {
    return preserveInitialTitle
      ? prepareEditableSurveySchema(initialSchema, initialName)
      : prepareImportedSurveySchema(initialSchema, initialName)
  }
  const next = createEmptySurveySchema()
  next.title = initialName || ''
  return next
}
export function validateSurveySchema(schema) {
  const normalized = prepareImportedSurveySchema(schema, '')
  normalized.title = String(schema?.title || '').trim()
  normalized.description = String(schema?.description || '').trim()
  if (!normalized.title) {
    return { error: '설문 제목을 입력해 주세요.', schema: normalized }
  }
  if (normalized.sections.length === 0) {
    return { error: '설문 항목을 하나 이상 추가해 주세요.', schema: normalized }
  }
  const questionCount = normalized.sections.reduce((total, section) => total + (Array.isArray(section.questions) ? section.questions.length : 0), 0)
  if (questionCount === 0) {
    return { error: '질문을 하나 이상 추가해 주세요.', schema: normalized }
  }

  const sectionKeys = new Set()
  const questionKeys = new Set()
  for (let sectionIndex = 0; sectionIndex < normalized.sections.length; sectionIndex += 1) {
    const section = normalized.sections[sectionIndex]
    section.title = String(section.title || '').trim()
    section.description = String(section.description || '').trim()
    if (!section.title) {
      return { error: `${sectionIndex + 1}번째 설문 항목의 제목을 입력해 주세요.`, schema: normalized }
    }
    let sectionKey = String(section.key || '').trim()
    if (!sectionKey || sectionKeys.has(sectionKey)) sectionKey = makeKey('section', sectionKeys)
    section.key = sectionKey
    sectionKeys.add(sectionKey)

    const questions = Array.isArray(section.questions) ? section.questions : []
    section.questions = questions.map((question) => ({ ...question }))
    for (let questionIndex = 0; questionIndex < section.questions.length; questionIndex += 1) {
      const question = section.questions[questionIndex]
      question.label = String(question?.label || '').trim()
      question.type = String(question?.type || 'text').toLowerCase()
      question.helpText = String(question?.helpText || '').trim()
      question.placeholder = String(question?.placeholder || '').trim()
      question.required = question?.required === true
      if (!question.label) {
        return {
          error: `${section.title}의 ${questionIndex + 1}번째 질문을 입력해 주세요.`,
          schema: normalized,
        }
      }
      let questionKey = String(question?.key || '').trim()
      if (!questionKey || questionKeys.has(questionKey)) questionKey = makeKey('question', questionKeys)
      question.key = questionKey
      questionKeys.add(questionKey)

      if (isOptionQuestion(question.type)) {
        const options = Array.isArray(question.options) ? question.options : []
        const cleanedOptions = options
          .map(optionDisplayText)
          .filter(Boolean)
        if (cleanedOptions.length === 0) {
          return {
            error: `${question.label} 질문의 보기를 하나 이상 입력해 주세요.`,
            schema: normalized,
          }
        }
        question.options = cleanedOptions
      } else {
        delete question.options
      }
    }
  }
  return { error: '', schema: normalized }
}

function QuestionPreview({ question }) {
  const type = String(question?.type || 'text').toLowerCase()
  const options = Array.isArray(question?.options) ? question.options.map(optionDisplayText) : []
  const placeholder = question?.placeholder || (type === 'long-text' ? '응답을 입력합니다.' : '응답을 입력합니다.')

  if (type === 'single-select' || type === 'multi-select') {
    return (
      <div className="mt-3 grid gap-2 md:grid-cols-2" aria-hidden="true">
        {options.map((option, index) => (
          <label key={`${String(option)}-${index}`} className="flex items-center gap-2 rounded-xl border bg-white px-3 py-2 text-sm text-slate-600">
            <input type={type === 'single-select' ? 'radio' : 'checkbox'} disabled />
            <span>{option || `보기 ${index + 1}`}</span>
          </label>
        ))}
      </div>
    )
  }

  if (type === 'select') {
    return (
      <select className="mt-3 w-full rounded-lg border px-3 py-2 text-sm text-slate-500" disabled value="" aria-label="응답 미리보기">
        <option value="">선택해 주세요.</option>
        {options.map((option, index) => <option key={`${String(option)}-${index}`} value={String(option)}>{option || `보기 ${index + 1}`}</option>)}
      </select>
    )
  }

  if (type === 'long-text') {
    return <textarea className="mt-3 min-h-[88px] w-full rounded-lg border px-3 py-2 text-sm text-slate-400" disabled placeholder={placeholder} aria-label="응답 미리보기" />
  }

  return <input className="mt-3 w-full rounded-lg border px-3 py-2 text-sm text-slate-400" disabled type={type === 'tel' || type === 'date' || type === 'number' ? type : 'text'} placeholder={placeholder} aria-label="응답 미리보기" />
}

function QuestionInsertDivider({ sectionIndex, insertIndex, onInsert }) {
  return (
    <div className="survey-question-insert-row group relative flex h-12 items-center">
      <span
        className="h-px w-full bg-slate-300 transition-colors group-hover:bg-brand-sky/80 group-focus-within:bg-brand-sky/80"
        aria-hidden="true"
      />
      <button
        type="button"
        className="survey-question-insert-action absolute right-2 inline-flex min-h-10 items-center overflow-hidden rounded-full border border-brand-sky/60 bg-white px-3 py-1.5 text-xs font-semibold text-brand-navy shadow-sm transition hover:border-brand-sky hover:bg-sky-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky/40"
        aria-label={`파트 ${sectionIndex + 1}, 질문 ${insertIndex} 다음에 질문 추가`}
        onClick={() => onInsert(sectionIndex, insertIndex)}
      >
        <span className="text-base leading-none" aria-hidden="true">+</span>
        <span className="survey-question-insert-label">질문 추가</span>
      </button>
    </div>
  )
}

function SurveyQuestionEditor({ question, questionIndex, sectionIndex, onChange, onDelete, onMove }) {
  const type = String(question?.type || 'text').toLowerCase()
  const options = Array.isArray(question?.options) ? question.options.map(optionDisplayText) : []
  const update = (patch) => onChange(sectionIndex, questionIndex, patch)

  const updateType = (nextType) => {
    const patch = { type: nextType }
    if (isOptionQuestion(nextType) && options.length === 0) patch.options = ['보기 1', '보기 2']
    if (!isOptionQuestion(nextType)) patch.options = undefined
    update(patch)
  }

  const updateOption = (optionIndex, value) => {
    const nextOptions = [...options]
    nextOptions[optionIndex] = value
    update({ options: nextOptions })
  }

  const addOption = () => update({ options: [...options, `보기 ${options.length + 1}`] })
  const deleteOption = (optionIndex) => update({ options: options.filter((_, index) => index !== optionIndex) })

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex flex-wrap items-start gap-2">
        <div className="mt-0.5 rounded-md bg-brand-navy px-2 py-1 text-xs font-semibold text-white">Q{questionIndex + 1}</div>
        <label className="min-w-0 flex-1">
          <span className="sr-only">질문 {questionIndex + 1}</span>
          <input
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-medium text-slate-900 placeholder:text-slate-400 focus:border-brand-sky focus:outline-none"
            value={question?.label || ''}
            placeholder="질문을 입력해 주세요."
            onChange={(event) => update({ label: event.target.value })}
          />
        </label>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <button type="button" className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40" onClick={() => onMove(sectionIndex, questionIndex, -1)} disabled={questionIndex === 0} aria-label={`질문 ${questionIndex + 1} 위로 이동`}>위로</button>
          <button type="button" className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 hover:bg-slate-50" onClick={() => onMove(sectionIndex, questionIndex, 1)} aria-label={`질문 ${questionIndex + 1} 아래로 이동`}>아래로</button>
          <button type="button" className="rounded-lg border border-rose-200 bg-white px-2.5 py-2 text-xs font-medium text-rose-700 hover:bg-rose-50" onClick={() => onDelete(sectionIndex, questionIndex)}>삭제</button>
        </div>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">응답 형식</span>
          <select className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm" value={type} onChange={(event) => updateType(event.target.value)}>
            {QUESTION_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label className="flex min-h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
          <input type="checkbox" checked={question?.required === true} onChange={(event) => update({ required: event.target.checked })} />
          필수 응답
        </label>
      </div>

      <details className="mt-3 rounded-xl border border-slate-200 bg-white">
        <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-700">입력 안내 설정</summary>
        <div className="grid gap-3 border-t border-slate-100 p-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">도움말</span>
            <input className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" value={question?.helpText || ''} placeholder="질문 아래에 표시할 안내" onChange={(event) => update({ helpText: event.target.value })} />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">입력 예시</span>
            <input className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" value={question?.placeholder || ''} placeholder="응답 입력칸에 표시할 예시" onChange={(event) => update({ placeholder: event.target.value })} />
          </label>
        </div>
      </details>

      {isOptionQuestion(type) && (
        <div className="mt-3 rounded-xl border border-teal-100 bg-white p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-slate-700">보기</div>
            <button type="button" className="rounded-lg border border-dashed border-teal-300 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-800 hover:bg-teal-100" onClick={addOption}>보기 추가</button>
          </div>
          <div className="mt-2 space-y-2">
            {options.map((option, optionIndex) => (
              <div key={`${question?.key || 'new'}-${optionIndex}`} className="flex items-center gap-2">
                <span className="w-5 shrink-0 text-right text-xs text-slate-400">{optionIndex + 1}</span>
                <input className="min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm" value={typeof option === 'string' ? option : (option?.label || '')} placeholder={`보기 ${optionIndex + 1}`} onChange={(event) => updateOption(optionIndex, event.target.value)} />
                <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs text-slate-600 hover:bg-slate-50" onClick={() => deleteOption(optionIndex)} aria-label={`보기 ${optionIndex + 1} 삭제`}>삭제</button>
              </div>
            ))}
            {options.length === 0 && <p className="text-xs text-amber-800">보기를 하나 이상 추가해 주세요.</p>}
          </div>
        </div>
      )}

      <QuestionPreview question={question} />
    </div>
  )
}

export default function SurveySchemaBuilder({
  initialSchema,
  initialName = '',
  sourceLabel = '',
  preserveInitialTitle = false,
  onSave,
  onCancel,
  saving = false,
}) {
  const [schema, setSchema] = React.useState(() => buildInitialSurveySchema(
    initialSchema,
    initialName,
    preserveInitialTitle,
  ))
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    setSchema(buildInitialSurveySchema(initialSchema, initialName, preserveInitialTitle))
    setError('')
  }, [initialSchema, initialName, preserveInitialTitle])
  const questionCount = React.useMemo(
    () => (Array.isArray(schema?.sections) ? schema.sections : []).reduce((total, section) => total + (Array.isArray(section?.questions) ? section.questions.length : 0), 0),
    [schema],
  )

  const updateSchema = (updater) => {
    setSchema((previous) => {
      const next = typeof updater === 'function' ? updater(previous) : updater
      return next
    })
  }

  const updateSection = (sectionIndex, patch) => updateSchema((previous) => {
    const next = deepClone(previous)
    if (!next?.sections?.[sectionIndex]) return previous
    next.sections[sectionIndex] = { ...next.sections[sectionIndex], ...patch }
    return next
  })

  const addSection = () => updateSchema((previous) => {
    const next = deepClone(previous || createEmptySurveySchema())
    if (!Array.isArray(next.sections)) next.sections = []
    const sectionKey = makeKey('section', allSectionKeys(next))
    next.sections.push({ key: sectionKey, title: '새 설문 항목', description: '', questions: [] })
    return next
  })

  const moveSection = (sectionIndex, direction) => updateSchema((previous) => {
    const next = deepClone(previous)
    const targetIndex = sectionIndex + direction
    if (!next?.sections || targetIndex < 0 || targetIndex >= next.sections.length) return previous
    const [section] = next.sections.splice(sectionIndex, 1)
    next.sections.splice(targetIndex, 0, section)
    return next
  })

  const deleteSection = (sectionIndex) => {
    const sectionTitle = schema?.sections?.[sectionIndex]?.title || '이 설문 항목'
    if (!window.confirm(`${sectionTitle}과(와) 안의 질문을 모두 삭제할까요?`)) return
    updateSchema((previous) => {
      const next = deepClone(previous)
      next.sections.splice(sectionIndex, 1)
      return next
    })
  }

  const addQuestion = (sectionIndex, insertIndex) => updateSchema((previous) => {
    const next = deepClone(previous)
    const section = next?.sections?.[sectionIndex]
    if (!section) return previous
    const questionKey = makeKey('question', allQuestionKeys(next))
    if (!Array.isArray(section.questions)) section.questions = []
    const targetIndex = Number.isInteger(insertIndex)
      ? Math.min(Math.max(insertIndex, 0), section.questions.length)
      : section.questions.length
    section.questions.splice(targetIndex, 0, { key: questionKey, type: 'text', label: '새 질문', required: false, helpText: '', placeholder: '' })
    return next
  })

  const updateQuestion = (sectionIndex, questionIndex, patch) => updateSchema((previous) => {
    const next = deepClone(previous)
    const question = next?.sections?.[sectionIndex]?.questions?.[questionIndex]
    if (!question) return previous
    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined) delete question[key]
      else question[key] = value
    })
    return next
  })

  const deleteQuestion = (sectionIndex, questionIndex) => {
    if (!window.confirm('이 질문을 삭제할까요?')) return
    updateSchema((previous) => {
      const next = deepClone(previous)
      next.sections?.[sectionIndex]?.questions?.splice(questionIndex, 1)
      return next
    })
  }

  const moveQuestion = (sectionIndex, questionIndex, direction) => updateSchema((previous) => {
    const next = deepClone(previous)
    const questions = next?.sections?.[sectionIndex]?.questions
    const targetIndex = questionIndex + direction
    if (!questions || targetIndex < 0 || targetIndex >= questions.length) return previous
    const [question] = questions.splice(questionIndex, 1)
    questions.splice(targetIndex, 0, question)
    return next
  })

  const handleSave = () => {
    const result = validateSurveySchema(schema)
    if (result.error) {
      setError(result.error)
      return
    }
    setError('')
    onSave?.({ name: result.schema.title, schema: result.schema })
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="overflow-hidden rounded-3xl border bg-brand-navy text-white shadow-sm">
        <div className="bg-[linear-gradient(135deg,rgba(255,255,255,0.08),transparent_55%)] p-5 sm:p-6">
          <div className="text-xs font-semibold tracking-[0.24em] text-amber-300">CUSTOMER SURVEY</div>
          <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <label className="block">
                <span className="sr-only">설문 제목</span>
                <input className="w-full border-0 border-b border-white/30 bg-transparent px-0 py-1 text-2xl font-bold text-white placeholder:text-white/45 focus:border-sky-300 focus:outline-none" value={schema?.title || ''} placeholder="설문 제목" onChange={(event) => updateSchema((previous) => ({ ...previous, title: event.target.value }))} />
              </label>
              <label className="mt-3 block">
                <span className="sr-only">설문 설명</span>
                <textarea className="min-h-[72px] w-full resize-y rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-white/55 focus:border-sky-300 focus:outline-none" value={schema?.description || ''} placeholder="설문 안내 문구" onChange={(event) => updateSchema((previous) => ({ ...previous, description: event.target.value }))} />
              </label>
              {sourceLabel && <div className="mt-2 text-xs text-white/65">선택한 항목으로 새 설문을 만들고 있습니다. 원본: {sourceLabel}</div>}
            </div>
            <div className="min-w-[10rem] rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm">
              <div className="text-xs text-white/70">편집 현황</div>
              <div className="mt-1 font-semibold">{schema?.sections?.length || 0}개 항목 · {questionCount}개 질문</div>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" role="alert" aria-live="polite">{error}</div>}

      {(Array.isArray(schema?.sections) ? schema.sections : []).map((section, sectionIndex) => (
        <section key={section.key || sectionIndex} className="rounded-3xl border bg-white p-4 shadow-sm sm:p-5">
          <div className="flex flex-wrap items-start gap-3">
            <div className="rounded-xl bg-brand-navy px-3 py-2 text-xs font-semibold tracking-[0.18em] text-white">PART {String(sectionIndex + 1).padStart(2, '0')}</div>
            <div className="min-w-0 flex-1 space-y-2">
              <label className="block">
                <span className="sr-only">설문 항목 제목</span>
                <input className="w-full border-0 border-b border-slate-200 px-0 py-1 text-lg font-semibold text-slate-900 placeholder:text-slate-400 focus:border-brand-sky focus:outline-none" value={section.title || ''} placeholder="설문 항목 제목" onChange={(event) => updateSection(sectionIndex, { title: event.target.value })} />
              </label>
              <label className="block">
                <span className="sr-only">설문 항목 설명</span>
                <input className="w-full border-0 bg-transparent px-0 py-1 text-sm text-slate-600 placeholder:text-slate-400 focus:outline-none" value={section.description || ''} placeholder="항목 설명 (선택)" onChange={(event) => updateSection(sectionIndex, { description: event.target.value })} />
              </label>
            </div>
            <div className="ml-auto flex flex-wrap gap-1.5">
              <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40" onClick={() => moveSection(sectionIndex, -1)} disabled={sectionIndex === 0} aria-label={`${section.title || '설문 항목'} 위로 이동`}>위로</button>
              <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs text-slate-700 hover:bg-slate-50" onClick={() => moveSection(sectionIndex, 1)} aria-label={`${section.title || '설문 항목'} 아래로 이동`}>아래로</button>
              <button type="button" className="rounded-lg border border-rose-200 px-2.5 py-2 text-xs text-rose-700 hover:bg-rose-50" onClick={() => deleteSection(sectionIndex)}>항목 삭제</button>
            </div>
          </div>

          <div className="mt-4">
            {(Array.isArray(section.questions) ? section.questions : []).map((question, questionIndex, questions) => (
              <React.Fragment key={question.key || questionIndex}>
                <SurveyQuestionEditor question={question} questionIndex={questionIndex} sectionIndex={sectionIndex} onChange={updateQuestion} onDelete={deleteQuestion} onMove={moveQuestion} />
                {questionIndex < questions.length - 1 && (
                  <QuestionInsertDivider
                    sectionIndex={sectionIndex}
                    insertIndex={questionIndex + 1}
                    onInsert={addQuestion}
                  />
                )}
              </React.Fragment>
            ))}
            {(Array.isArray(section.questions) ? section.questions : []).length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">아직 질문이 없습니다. 아래 버튼으로 질문을 추가해 주세요.</div>
            )}
            <button type="button" className="mt-4 w-full rounded-xl border border-dashed border-brand-sky/50 bg-sky-50 px-4 py-3 text-sm font-semibold text-brand-navy hover:bg-sky-100 sm:w-auto" aria-label={`파트 ${sectionIndex + 1} 마지막에 질문 추가`} onClick={() => addQuestion(sectionIndex)}>질문 추가</button>
          </div>
        </section>
      ))}

      <button type="button" className="w-full rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-4 text-sm font-semibold text-slate-700 hover:bg-slate-50" onClick={addSection}>설문 항목 추가</button>

      <div className="sticky bottom-0 z-10 rounded-2xl border bg-white/95 p-4 backdrop-blur">
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className="rounded-lg border px-4 py-2 text-sm hover:bg-slate-50" onClick={onCancel} disabled={saving}>취소</button>
          <button type="button" className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '설문 저장'}</button>
        </div>
      </div>
    </div>
  )
}
